import { language_enum, role_enum } from '@predium/enums';
import { getLanguageLocale, PreTFunction } from '@predium/i18n/client';
import { Localize } from '@predium/utils';
import { useSelector } from '@xstate/store-react';
import isNil from 'lodash/isNil';
import {
  createContext,
  Dispatch,
  Fragment,
  PropsWithChildren,
  SetStateAction,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { sessionStore } from '@/components/session/session.store';
import { setYupLocale } from '@/utils/extendedYup/yupI18n';
import i18n, { DEFAULT_LANGUAGE } from '../../../i18n';
import { t as originalT } from './t';

const I18nContext = createContext<{
  language: language_enum;
  setLanguage: Dispatch<SetStateAction<language_enum>>;
  localize: Localize;
  t: PreTFunction;
} | null>(null);

export const I18nProvider = ({
  children,
  defaultLanguage = DEFAULT_LANGUAGE,
}: PropsWithChildren<{ defaultLanguage?: language_enum }>) => {
  const [language, setLanguageState] = useState(defaultLanguage);
  const locale = getLanguageLocale(language);
  const localize = useMemo(() => new Localize(locale, originalT), [locale]);

  const t = useCallback(originalT, [locale]);

  // Switch i18next's locale synchronously with React state so that when children re-render
  // with the new `language` in context, `t(...)` already returns strings in the new language.
  // Doing this in a useEffect would fire after the render commits, leaving one render cycle
  // where children see new language in context but stale translations from i18next — that
  // cycle caches stale strings in any useMemo on the way down.
  const setLanguage = useCallback<Dispatch<SetStateAction<language_enum>>>((action) => {
    setLanguageState((prev) => {
      const next = typeof action === 'function' ? (action as (p: language_enum) => language_enum)(prev) : action;
      const nextLocale = getLanguageLocale(next);
      if (i18n.language !== nextLocale) {
        i18n.changeLanguage(nextLocale);
        document.documentElement.lang = nextLocale;
      }
      return next;
    });
  }, []);

  // Adopt the user's preferred language from the session once, on first hydration.
  // Subsequent session polls must not override a language the user has explicitly picked
  // via LanguageSelect (the mutation persists their choice server-side anyway).
  //
  // Exception: on the public shared snapshot route the session is hydrated twice —
  // once with hardcoded defaults at module-init (so Router can render before the
  // snapshot loads) and again with the snapshot author's language once the GraphQL
  // query lands. The anonymous user has no LanguageSelect to override, so the
  // "adopt once" guard is keyed on role: DANGER_PUBLIC_ROLE → re-adopt every change.
  const sessionLanguage = useSelector(sessionStore, (s) =>
    s.context.status === 'authenticated' ? (s.context.user?.language ?? null) : null,
  );
  const sessionRole = useSelector(sessionStore, (s) =>
    s.context.status === 'authenticated' ? (s.context.user?.role ?? null) : null,
  );
  const hasAdoptedSessionLanguage = useRef(false);
  useEffect(() => {
    if (isNil(sessionLanguage)) return;
    const isAnonymous = sessionRole === role_enum.DANGER_PUBLIC_ROLE;
    if (isAnonymous || !hasAdoptedSessionLanguage.current || sessionLanguage !== language) {
      hasAdoptedSessionLanguage.current = true;
      setLanguage(sessionLanguage);
    }
  }, [sessionLanguage, sessionRole, setLanguage, language]);

  useEffect(() => {
    localize.setLocale(locale);
    setYupLocale({ localize, language });
  }, [locale, localize, language]);

  // Remount the entire subtree when `language` changes. Any component below that cached
  // translated strings in a useMemo/useState without declaring language as a dep would
  // otherwise keep serving the previous language's strings. Remounting makes it impossible
  // for stale translations to leak past a language switch, regardless of how individual
  // components memoize. Paired with the synchronous i18n.changeLanguage above, this means
  // the fresh mount's first render already sees the new language in every t(...) call.
  return (
    <I18nContext.Provider value={{ language, setLanguage, localize, t }}>
      <Fragment key={language}>{children}</Fragment>
    </I18nContext.Provider>
  );
};

export const useI18n = () => {
  const context = useContext(I18nContext);
  if (!context) {
    throw new Error('useI18n must be used within a I18nProvider');
  }

  return context;
};
